import { useRef, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { X } from "lucide-react";

import { IconButton, useModalFocus } from "@nexus/ui";
import { cn } from "@/lib/utils";
import { Logo } from "./Logo";
import {
  getActiveNavGroup,
  getNavGroupEntryHref,
  useAuthorizedNavGroups,
  useNavActiveState,
} from "./NavSidebar";
import { SidebarNavItems } from "./SidebarNavItems";
import { UserProfile } from "./UserProfile";

interface MobileNavigationDrawerProps {
  open: boolean;
  onClose: () => void;
  controls?: ReactNode;
}

export function MobileNavigationDrawer({ open, onClose, controls }: MobileNavigationDrawerProps) {
  const location = useLocation();
  const dialogRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const authorizedGroups = useAuthorizedNavGroups();
  const activeGroup = getActiveNavGroup(location.pathname, authorizedGroups);
  const { activeItems, activeItemId } = useNavActiveState(location.pathname, activeGroup);

  useModalFocus({
    open,
    onClose,
    dialogRef,
    initialFocusRef: closeRef,
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 xl:hidden" role="presentation">
      <button
        type="button"
        className="absolute inset-0 bg-background/70 backdrop-blur-[2px]"
        aria-label="Close navigation"
        onClick={onClose}
      />
      <aside
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Application navigation"
        tabIndex={-1}
        className="relative flex h-full w-[min(22rem,calc(100vw-2rem))] flex-col overflow-hidden border-r border-border bg-surface shadow-xl"
      >
        <div className="flex h-14 shrink-0 items-center gap-3 border-b border-border px-4">
          <Logo variant="full" size={34} />
          <IconButton ref={closeRef} label="Close navigation" className="ml-auto" onClick={onClose}>
            <X className="h-4 w-4" aria-hidden />
          </IconButton>
        </div>

        <nav className="shrink-0 border-b border-border p-3" aria-label="Spaces">
          <ul className="grid grid-cols-2 gap-1">
            {authorizedGroups.map((group) => {
              const Icon = group.icon;
              const active = group.id === activeGroup.id;

              return (
                <li key={group.id}>
                  <Link
                    to={getNavGroupEntryHref(group)}
                    onClick={onClose}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "flex h-10 items-center gap-2 rounded-lg px-3 text-sm font-medium transition-colors",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus",
                      active
                        ? "bg-brand-cyan/10 text-primary"
                        : "text-secondary hover:bg-surface-elevated hover:text-primary",
                    )}
                  >
                    <Icon className="h-4 w-4" aria-hidden />
                    {group.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
          <p className="mb-2 px-3 text-xs font-semibold uppercase text-muted">
            {activeGroup.label}
          </p>
          <SidebarNavItems
            items={activeItems}
            activeItemId={activeItemId}
            expanded
            className="flex flex-col"
            onNavigate={onClose}
          />
        </div>

        <div className="shrink-0 space-y-3 border-t border-border p-3">
          {controls}
          <UserProfile side="top" align="start" onOpenSettings={onClose} />
        </div>
      </aside>
    </div>
  );
}
